import { Controller } from "stimulus";
import { getMetaValue } from "helpers";
import Tabulator from 'tabulator-tables';
import AutoNumeric from 'autonumeric';
import { Draggable } from 'agnostic-draggable';

// phones and tablets: the same breakpoint as maxmd in the styles and as layout--sidebar
export const MOBILE_LAYOUT_QUERY = "(max-width: 991px)";
// tablets only: the same range as smtomd in the styles
export const TABLET_LAYOUT_QUERY = "(min-width: 768px) and (max-width: 991px)";

export class ApplicationController extends Controller {

  get isMobileLayout() {
    return window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
  }

  get isTabletLayout() {
    return window.matchMedia(TABLET_LAYOUT_QUERY).matches;
  }

  // Calls back with true/false when the layout switches between mobile and desktop (a tablet is rotated).
  // Returns the function that removes the listener: call it in disconnect().
  onMobileLayoutChange(callback) {
    const media = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const listener = event => callback(event.matches);
    if (media.addEventListener) media.addEventListener("change", listener);
    else media.addListener(listener);

    return () => {
      if (media.removeEventListener) media.removeEventListener("change", listener);
      else media.removeListener(listener);
    };
  }

  // Tabulator props of the card mode of a list on phones and tablets: one column whose formatter builds a card
  // from the visible columns (the ones the user picked on the computer). The place of a value in the card comes
  // from card_role in Columns::* (cardRole in the columns JSON): title, status, amount, muted; by default the
  // first column is the title and the right aligned ones are amounts. Merge it over the props of the page and
  // add the tabulator--cards class to the table element.
  tabulatorCardModeProps(columns) {
    const card_columns = columns.filter(column => column.field && column.visible !== false &&
      column.formatter !== "rowSelection" && !["index", "table_action_links"].includes(column.field));
    const index_column = columns.find(column => column.field === "index");
    // the sortable columns stay in the table hidden: Tabulator ignores a sort by a field it has no column for,
    // and the initial sort, the sort select of the cards and the sort kept on a rotation refer to them
    const sort_columns = columns.filter(column => column.field && column.headerSort)
      .map(column => ({ field: column.field, title: "", visible: false, headerSort: true }));

    return {
      columns: [{
        field: "__card",
        headerSort: false,
        formatter: cell => this.tabulatorCardHtml(cell.getRow().getData(), card_columns, index_column)
      }].concat(sort_columns),
      headerVisible: false,
      layout: "fitColumns",
      movableColumns: false,
      resizableColumns: false,
      height: false,
      maxHeight: false,
      virtualDom: false,
      virtualDomHoz: false,
      // the columns saved for the desktop table must not be overwritten by the single card column
      persistence: false,
      paginationButtonCount: 3,
      paginationSizeSelector: false
    };
  }

  tabulatorCardHtml(data, columns, index_column = null) {
    // html columns come from the server already escaped (jb + link_to), plaintext ones may hold user input
    const value = column => {
      const raw = data[column.field];
      if (raw === null || raw === undefined || raw === "") return "";
      if (column.formatter !== "html") return this.escapeHtml(String(raw));
      // e.g. an actions menu whose items are all hidden by the policies, or the controls for a computer only
      const html = this.withoutDesktopOnly(String(raw));
      return this.htmlHasContent(html) ? html : "";
    };
    const role = column => column.cardRole || (column.hozAlign === "right" ? "amount" : "field");

    const title_column = columns.find(column => column.cardRole === "title") || columns[0];
    const index_icons = index_column ? this.withoutText(value(index_column)) : "";
    const index = index_icons ? `<span class="tabulator-card__index">${index_icons}</span>` : "";
    let status = "";
    let fields = "";
    let amounts = "";
    columns.filter(column => column !== title_column).forEach(column => {
      const html = value(column);
      if (!html) return;

      const labelled = `<span class="tabulator-card__label">${column.title}</span><span class="tabulator-card__value">${html}</span>`;
      if (role(column) === "status") status += `<span class="tabulator-card__status">${html}</span>`;
      else if (role(column) === "amount") amounts += `<div class="tabulator-card__amount">${labelled}</div>`;
      else if (role(column) === "muted") fields += `<div class="tabulator-card__field tabulator-card__field--wide text-muted">${labelled}</div>`;
      else fields += `<div class="tabulator-card__field">${labelled}</div>`;
    });

    return `<div class="tabulator-card">` +
      `<div class="tabulator-card__head">${index}<span class="tabulator-card__title">${title_column ? value(title_column) : ""}</span>${status}</div>` +
      `<div class="tabulator-card__fields">${fields}</div>` +
      (amounts ? `<div class="tabulator-card__amounts">${amounts}</div>` : "") +
      `</div>`;
  }

  // A card shows no row number: it is hard to read on a card and the list says as much. The elements next to it
  // stay (the status icon of a visa is in the index column): the text between the top level elements is dropped.
  withoutText(html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    Array.from(template.content.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .forEach(node => node.remove());
    return template.innerHTML.trim();
  }

  // the parts of a cell marked .desktop-only (the controls and the downloads) are not shown on a card
  withoutDesktopOnly(html) {
    if (!html.includes("desktop-only")) return html;
    const template = document.createElement("template");
    template.innerHTML = html;
    template.content.querySelectorAll(".desktop-only").forEach(element => element.remove());
    return template.innerHTML;
  }

  htmlHasContent(html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content.textContent.trim() !== "" ||
      template.content.querySelector("img, svg, i, input, select") !== null;
  }

  escapeHtml(text) {
    const element = document.createElement("div");
    element.textContent = text;
    return element.innerHTML;
  }

  historyPush(url) {
    Turbolinks
      .controller
      .pushHistoryWithLocationAndRestorationIdentifier(url, Turbolinks.uuid())

  }

  objectToQueryString(params) {
    return Object.keys(params).map(key => key + '=' + params[key]).join('&');
  }

  getControllerByIdentifier(identifier) {
    return this.application.controllers.find(controller => {
      return controller.context.identifier === identifier;
    });
  }

  toggleUpdate(url, field, value, toggle_target) {
    return new Promise((resolve, reject) => {
      const data = new FormData();
      data.append(field, value);

      fetch(url, {
        body: data,
        method: "PUT",
        dataType: "script",
        credentials: "include",
        headers: {
          "X-CSRF-Token": getMetaValue("csrf-token")
        },
      }).then(function (response) {
        if (response.status != 204) {
          toggle_target.checked = !toggle_target.checked
        }
      })
    })
  }

  //
  //
  //
  setNumericFormat(value) {
    try {
      if (!value)
        return 0;
      return AutoNumeric.format(value, {
        allowDecimalPadding: "floats",
        decimalPlaces: 2,
        digitGroupSeparator: " ",
        decimalCharacter: ".",
        decimalCharacterAlternative: ",",
        styleRules: {
          positive: "autoNumeric-positive"
        },
        minimumValue: 0
      });
    } catch (err) {
      return 0;
    }
  }

  draggable_modal(query_selector) {
    let modal_win = document.querySelector(query_selector);
    let appendTo = modal_win.parentElement.parentElement;
    // new Draggable(modal_win, {appendTo: appendTo, containment: 'window', cursor: 'move'});
    new Draggable(modal_win, {appendTo: appendTo, axis: 'x', cursor: 'move'});
  }

  // open modal and load modal content from url
  // let url = this.data.get("new-url");
  // this.openLayoutModal(url);
  async openLayoutModal(content_url) {
    // await - https://learn.javascript.ru/async-await#await
    let content_response = await fetch(content_url);
    let content = await content_response.text();
    //
    // https://thednp.github.io/bootstrap.native/#componentModal
    //
    if (window.layout_modal) {
      window.layout_modal.setContent(content);
      window.layout_modal.show();
      this.draggable_modal('#layout_modal .modal-content');
    }
  }

  async openLayoutModalMD(content_url) {
    // await - https://learn.javascript.ru/async-await#await
    let content_response = await fetch(content_url);
    let content = await content_response.text();
    //
    // https://thednp.github.io/bootstrap.native/#componentModal
    //
    if (window.layout_modal_md) {
      window.layout_modal_md.setContent(content);
      window.layout_modal_md.show();
      this.draggable_modal('#layout_modal_md .modal-content');
    }
  }

  async openLayoutModalLG(content_url) {
    // await - https://learn.javascript.ru/async-await#await
    let content_response = await fetch(content_url);
    let content = await content_response.text();
    //
    // https://thednp.github.io/bootstrap.native/#componentModal
    //
    if (window.layout_modal_lg) {
      window.layout_modal_lg.setContent(content);
      window.layout_modal_lg.show();
      this.draggable_modal('#layout_modal_lg .modal-content');
    }
  }

  async openLayoutModalXL(content_url) {
    // await - https://learn.javascript.ru/async-await#await
    let content_response = await fetch(content_url);
    let content = await content_response.text();
    //
    // https://thednp.github.io/bootstrap.native/#componentModal
    //
    if (window.layout_modal_xl) {
      window.layout_modal_xl.setContent(content);
      window.layout_modal_xl.show();
      this.draggable_modal('#layout_modal_xl .modal-content');
    }
  }

  async openGruopLayoutModalXL(content_url, ids) {
    // await - https://learn.javascript.ru/async-await#await

    // query
    let params = {
      ids: ids
    }

    content_url += '?' + this.objectToQueryString(params);

    let content_response = await fetch(content_url);
    let content = await content_response.text();
    //
    // https://thednp.github.io/bootstrap.native/#componentModal
    //
    if (window.layout_modal_xl) {
      window.layout_modal_xl.setContent(content);
      window.layout_modal_xl.show();
      this.draggable_modal('#layout_modal_xl .modal-content');
    }
  }


  setTabulatorTable() {
    let table = new Tabulator("#tabulator-table", {
      height: "100%",
      dataTree: true,
      dataTreeStartExpanded: true,
      layout: "fitColumns",
      resizableRows: true
    });

    return table;
  }

  async updateSidebarStick() {
    const sidebar = document.querySelector('#sidebar');
    // on phones and tablets layout--sidebar does not create the sticky sidebar
    if (sidebar && sidebar.sidebar) {
      await sidebar.sidebar.updateSticky();
      // console.log("updateSidebarStick")
    }
  }

  get appLocale() {
    return document.body.getAttribute("data-lang");
  }

  preferredCountries() {
    return JSON.parse(document.body.getAttribute("data-preferred-countries"));
  }

  get tabulatorDefaultProps() {
    let props = {
      // language
      langs: {
        "ru": {
          "pagination": {
            "page_size": "Кол-во на странице", //label for the page size select element
            "page_title": "Количество записей на странице",//tooltip text for the numeric page button, appears in front of the page number (eg. "Show Page" will result in a tool tip of "Show Page 1" on the page 1 button)
            "first": "Первая", //text for the first page button
            "first_title": "Первая страница", //tooltip text for the first page button
            "last": "Последная",
            "last_title": "Последная страница",
            "prev": "Предыдущий",
            "prev_title": "Предыдущая страница",
            "next": "Следующий",
            "next_title": "Следующя страница",
            "all": "Все",
          },
          "ajax": {
            "loading": "Загрузка...", //ajax loader text
            "error": "Ошибка", //ajax error text
          }
        },
        "en": {
          "pagination": {
            "page_size": "Page Size", //label for the page size select element
            "page_title": "Show Page",//tooltip text for the numeric page button, appears in front of the page number (eg. "Show Page" will result in a tool tip of "Show Page 1" on the page 1 button)
            "first": "First", //text for the first page button
            "first_title": "First Page", //tooltip text for the first page button
            "last": "Last",
            "last_title": "Last Page",
            "prev": "Prev",
            "prev_title": "Prev Page",
            "next": "Next",
            "next_title": "Next Page",
            "all": "All",
          },
          "ajax": {
            "loading": "Loading...", //ajax loader text
            "error": "Error", //ajax error text
          }
        },
      },
      // layout
      persistence: {
        sort: true,
        filter: false,
        group: false,
        page: {
          size: true, //persist the current page size
          page: false, //do not persist the current page
        },
        columns: true
      },
      layout: "fitDataFill",
      virtualDom: true,
      virtualDomHoz: true,
      resizableColumns: "header",
      resizableRows: false,
      maxHeight: "100%",
      autoResize: true
    }
    return props
  }

}

Tabulator.prototype.extendModule("format", "formatters", {
  column_wrap:function(cell, formatterParams){
    cell.getElement().classList.add("column-wrap");
    return cell.getValue();
  },
});